/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * MA6000-G2 Hierarchy Builder
 * Uses parentKey as primary parent linkage with topology route fallback.
 */

import { IInventoryGraphNode, IRemoteEquipment } from "../../../store/api/devices/types";
import { TopologyNode, DasNodeType, DasNodeStatus, HierarchyBuilder } from "../types";

interface ObjectKeyParts {
  targetSerial: string;
  route?: string;
}

export class MA6000G2HierarchyBuilder implements HierarchyBuilder {
  private equipmentMap: Map<string, IRemoteEquipment>;
  private equipmentList: IRemoteEquipment[];
  private processedNodes: Set<string>;

  constructor() {
    this.equipmentMap = new Map();
    this.equipmentList = [];
    this.processedNodes = new Set();
  }

  setEquipmentData(equipment: IRemoteEquipment[]) {
    this.equipmentMap.clear();
    this.equipmentList = [];

    const seen = new Set<string>();
    equipment.forEach((eq) => {
      const dedupKey = eq.objectKey || eq.serialNumber || `${eq.equipmentType}-${eq.hostname}-${eq.ipAddress}`;
      if (!seen.has(dedupKey)) {
        this.equipmentList.push(eq);
        seen.add(dedupKey);
      }

      if (eq.objectKey) this.equipmentMap.set(eq.objectKey, eq);
      if (eq.serialNumber) this.equipmentMap.set(eq.serialNumber, eq);
    });
  }

  parseStatus(status: string): DasNodeStatus {
    const statusLower = (status || "").toLowerCase();
    switch (statusLower) {
      case "connected":
      case "online":
      case "normal":
        return DasNodeStatus.ONLINE;
      case "disconnected":
      case "offline":
        return DasNodeStatus.DISCONNECTED;
      case "clear":
      case "cleared":
        return DasNodeStatus.CLEAR;
      case "minor":
        return DasNodeStatus.MINOR;
      case "major":
      case "warning":
        return DasNodeStatus.MAJOR;
      case "critical":
      case "error":
        return DasNodeStatus.CRITICAL;
      default:
        return DasNodeStatus.UNKNOWN;
    }
  }

  parseNodeType(node: IInventoryGraphNode): DasNodeType {
    const equipmentType = (node.equipmentType || node.type || "").toUpperCase();

    // MA6000-G2 site/controller family
    if (
      equipmentType.includes("DCU") ||
      equipmentType.includes("SDCU") ||
      equipmentType.includes("S-DCU")
    ) {
      return DasNodeType.SITE;
    }

    // MA6000-G2 RIU family
    if (
      equipmentType.includes("RIU") ||
      equipmentType.includes("SRIU") ||
      equipmentType.includes("S-RIU")
    ) {
      return DasNodeType.RAU;
    }

    // MA6000-G2 remote optical units
    if (
      equipmentType.includes("DEU") ||
      equipmentType.includes("DXRU") ||
      equipmentType.includes("DXRU10G") ||
      equipmentType.includes("DXRU-10G")
    ) {
      return DasNodeType.OCH;
    }

    return DasNodeType.SITE;
  }

  private normalizeRoute(route?: string): string {
    const raw = (route || "").trim();
    if (!raw) return "";
    if (raw === "0") return "0.0.0.0";
    if (/^\d+$/.test(raw)) return `${raw}.0.0.0`;

    const parts = raw.split(".");
    if (parts.every((p) => /^\d+$/.test(p)) && parts.length < 4) {
      return [...parts, ...Array(4 - parts.length).fill("0")].join(".");
    }

    return raw;
  }

  private parseObjectKey(objectKey: string): ObjectKeyParts {
    if (!objectKey) return { targetSerial: "" };

    const parts = objectKey.split(".");
    const equipment = this.equipmentMap.get(objectKey);

    return {
      targetSerial: parts[0] || "",
      route: equipment?.topology,
    };
  }

  private parseDxruTopology(topology?: string): { deuKey: string; portPart: string } {
    const raw = (topology || "").trim();
    if (!raw) return { deuKey: "", portPart: "" };

    const slashIndex = raw.indexOf("/");
    if (slashIndex === -1) return { deuKey: "", portPart: raw };

    return {
      deuKey: raw.slice(0, slashIndex).trim(),
      portPart: raw.slice(slashIndex + 1).trim(),
    };
  }

  private normalizeDeuDisplay(topology?: string): string {
    const raw = (topology || "").trim();
    if (!raw) return "0";
    return raw.replace(/^DEU\s*/i, "").trim() || "0";
  }

  private normalizeRiuDisplay(topology?: string): string {
    const raw = (topology || "").trim();
    if (!raw) return "0";
    return raw.replace(/^RIU\s*/i, "").trim() || "0";
  }

  private isRootDcu(eq: IRemoteEquipment): boolean {
    const type = (eq.equipmentType || "").toUpperCase();
    if (!type.includes("DCU")) return false;

    const route = this.normalizeRoute(eq.topology);
    return route === "0.0.0.0" || route === "";
  }

  private createTopologyNode(equipment: IRemoteEquipment, isRoot = false): TopologyNode {
    const dummy: IInventoryGraphNode = {
      id: equipment.objectKey || equipment.serialNumber || "",
      label: equipment.hostname || "",
      equipmentType: equipment.equipmentType || "",
    };

    let label = equipment.equipmentType || "Unknown";

    if (isRoot) {
      label = "MA6000-G2";
    } else {
      const equipmentType = (equipment.equipmentType || "").toUpperCase();
      const topology = (equipment.topology || "").trim();

      if (equipmentType.includes("DXRU")) {
        const { portPart } = this.parseDxruTopology(topology);
        const baseLabel = equipmentType.includes("10G") ? "DxRU10G" : "DxRU";
        label = `${baseLabel} ${portPart || topology || equipment.equipmentIndex || "0"}`;
      } else if (equipmentType.includes("RIU")) {
        label = `RIU-${this.normalizeRiuDisplay(topology || equipment.equipmentIndex || "0")}`;
      } else if (equipmentType.includes("DEU")) {
        label = `DEU - ${this.normalizeDeuDisplay(topology || equipment.equipmentIndex || "0")}`;
      } else {
        const route = this.normalizeRoute(topology);
        if (route) {
          const routeParts = route.split(".");
          let port: string | null = null;
          for (let i = routeParts.length - 1; i >= 0; i--) {
            if (routeParts[i] !== "0") {
              port = routeParts[i];
              break;
            }
          }
          if (port) label = `${equipment.equipmentType || "DEVICE"}.${port}`;
        }
      }
    }

    return {
      id: equipment.objectKey || equipment.serialNumber || "",
      label,
      type: this.parseNodeType(dummy),
      equipmentType: equipment.equipmentType || "",
      status: this.parseStatus(equipment.status || ""),
      model: equipment.model,
      ipAddress: equipment.ipAddress,
      serialNumber: equipment.serialNumber,
      managedObject: equipment.objectKey,
      children: [],
      metadata: { equipment },
    };
  }

  buildHierarchy(_nodes: IInventoryGraphNode[], _edges: any[], _rootId: string): TopologyNode | null {
    const equipment = this.equipmentList;
    this.processedNodes.clear();

    if (equipment.length === 0) return null;

    let rootDcu = equipment.find((eq) => this.isRootDcu(eq));
    if (!rootDcu) {
      rootDcu = equipment.find((eq) => (eq.equipmentType || "").toUpperCase().includes("DCU"));
    }
    if (!rootDcu) return null;

    const root = this.createTopologyNode(rootDcu, true);
    if (rootDcu.objectKey) this.processedNodes.add(rootDcu.objectKey);

    this.buildChildren(root, equipment);
    return root;
  }

  private normalizeComparable(value?: string): string {
    return (value || "").trim().toUpperCase();
  }

  private isDirectChild(parentRoute: string, childRoute: string): boolean {
    if (!parentRoute || !childRoute || parentRoute === childRoute) return false;

    const parentOctets = parentRoute.split(".");
    const childOctets = childRoute.split(".");
    if (parentOctets.length !== 4 || childOctets.length !== 4) return false;

    let parentLastNonZeroIndex = -1;
    let childNewLevelIndex = -1;
    let childNewLevelCount = 0;

    for (let index = 0; index < 4; index++) {
      const parentValue = parentOctets[index];
      const childValue = childOctets[index];

      if (parentValue !== "0") {
        if (parentValue !== childValue) return false;
        parentLastNonZeroIndex = index;
        continue;
      }

      if (childValue !== "0") {
        childNewLevelCount++;
        if (childNewLevelIndex === -1) childNewLevelIndex = index;
      }
    }

    if (childNewLevelCount !== 1) return false;
    return childNewLevelIndex > parentLastNonZeroIndex;
  }

  private isChildOfParent(parent: IRemoteEquipment, parentObjectKey: string, child: IRemoteEquipment): boolean {
    if (child.objectKey === parentObjectKey) return false;

    const childParentKey = this.normalizeComparable(child.parentKey);
    if (childParentKey) {
      const parentCandidates = new Set<string>([
        this.normalizeComparable(parent.objectKey),
        this.normalizeComparable(parent.managedObject),
        this.normalizeComparable(parent.serialNumber),
        this.normalizeComparable(parent.topology),
      ]);

      if (Array.from(parentCandidates).some((candidate) => !!candidate && candidate === childParentKey)) {
        return true;
      }

      // If parentKey is present but does not match this parent, do not
      // fall back to route-based linkage. parentKey has higher priority.
      return false;
    }

    const parentType = (parent.equipmentType || "").toUpperCase();
    const childType = (child.equipmentType || "").toUpperCase();
    if (parentType.includes("DEU") && childType.includes("DXRU")) {
      const { deuKey } = this.parseDxruTopology(child.topology);
      if (deuKey && this.normalizeComparable(deuKey) === this.normalizeComparable(parent.topology)) {
        return true;
      }
    }

    const parentParts = this.parseObjectKey(parentObjectKey);
    const childParts = this.parseObjectKey(child.objectKey || "");
    if (!parentParts.targetSerial || parentParts.targetSerial !== childParts.targetSerial) return false;

    const parentRoute = this.normalizeRoute(parent.topology || parentParts.route);
    const childRoute = this.normalizeRoute(child.topology || childParts.route);

    if (parentRoute && childRoute) {
      return this.isDirectChild(parentRoute, childRoute);
    }

    return false;
  }

  private buildChildren(parent: TopologyNode, equipment: IRemoteEquipment[]) {
    const parentObjectKey = parent.managedObject;
    if (!parentObjectKey) return;

    const parentEq = parent.metadata?.equipment as IRemoteEquipment | undefined;
    if (!parentEq) return;

    const children = equipment.filter((eq) => {
      if (!eq.objectKey || this.processedNodes.has(eq.objectKey)) return false;
      return this.isChildOfParent(parentEq, parentObjectKey, eq);
    });

    children.sort((a, b) => {
      const aRoute = this.normalizeRoute(a.topology);
      const bRoute = this.normalizeRoute(b.topology);
      if (aRoute && bRoute) {
        const aOctets = aRoute.split(".");
        const bOctets = bRoute.split(".");
        for (let i = 0; i < 4; i++) {
          const aVal = parseInt(aOctets[i] || "0", 10);
          const bVal = parseInt(bOctets[i] || "0", 10);
          if (aVal !== bVal) return aVal - bVal;
        }
      }

      return (a.objectKey || "").localeCompare(b.objectKey || "");
    });

    children.forEach((childEq) => {
      if (!childEq.objectKey) return;
      this.processedNodes.add(childEq.objectKey);

      const childNode = this.createTopologyNode(childEq, false);
      parent.children.push(childNode);
      this.buildChildren(childNode, equipment);
    });
  }
}

export const ma6000g2HierarchyBuilder = new MA6000G2HierarchyBuilder();
